import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Minimal agent.me-equivalent shape
const defaultAgent = {
  roles: {
    theme: {
      // UI-wide defaults (lowest priority)
      variant: 'default',
      shape: 'default',
      color: 'brand',
      size: 'md',
      align: 'left',
    },
    components: {}, // e.g., { Button: { defaultProps: { size: 'md' } } }
  },
};

const DSConfigContext = createContext({
  theme: 'light',
  setTheme: () => {},
  agent: defaultAgent,
});

const readThemeFromDOM = () => {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') || 'light';
};

const readAgentFromGlobal = () => {
  if (typeof window !== 'undefined' && window.__AGENT_ME__) return window.__AGENT_ME__;
  return defaultAgent;
};

export const useDesignSystem = () => useContext(DSConfigContext);

// Merge helper implementing precedence:
// props > slotProps > agent.components[name].defaultProps > agent.roles.theme > hardcoded
export const useComponentDefaults = (name, props = {}, hardcoded = {}, options = {}) => {
  const { agent } = useDesignSystem();
  const { demoteThemeKeys = [] } = options || {};
  const baseThemeDefaults = agent?.roles?.theme || {};
  const themeDefaults = demoteThemeKeys.length
    ? Object.fromEntries(
        Object.entries(baseThemeDefaults).filter(([key]) => !demoteThemeKeys.includes(key))
      )
    : baseThemeDefaults;
  const componentDefaults = agent?.roles?.components?.[name]?.defaultProps || {};

  const merged = useMemo(() => {
    const slot = props?.slotProps || {};
    return {
      ...hardcoded,
      ...themeDefaults,
      ...componentDefaults,
      ...(typeof slot === 'object' ? slot : {}),
      ...props,
    };
  }, [hardcoded, themeDefaults, componentDefaults, props]);

  return merged;
};

export default function DesignSystemProvider({ children, agent }) {
  const [theme, setTheme] = useState(readThemeFromDOM());
  const agentConfig = useMemo(() => agent || readAgentFromGlobal(), [agent]);

  // Ensure a consistent theme class is present for CSS targeting.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    // Do not fight upstream: if App or user already sets data-theme, respect it
    const current = html.getAttribute('data-theme');
    const next = current || theme || 'light';
    html.setAttribute('data-theme', next);
    // Add a utility class for theme-specific selectors if needed
    html.classList.remove('ds-theme--light', 'ds-theme--dark');
    html.classList.add(`ds-theme--${next}`);
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme, agent: agentConfig }), [theme, agentConfig]);

  return <DSConfigContext.Provider value={value}>{children}</DSConfigContext.Provider>;
}
